import { CARD_ENROLL_FAILURES, VISA } from '../constants/common-enums';

import { DEFAULT_VALUE } from '../constants/external-sdk-maps';
import { ENROLL_CARD } from '../constants/initiator-flow-enums';
import DcfLaunchData from '../lib/dcf/launch-data';
import EnrollCardService from '../lib/services/enroll-card-api';
import encryptConsumerObject from './encrypt-consumer-info';
import getCheckoutMessage from './get-checkout-message';

import getDpaTransactionOptionsForEnroll from './get-dpa-options-for-enroll';
import getErrorMessageFromResponse from './get-error-message';
import getRouteUserInDCFMessage from './get-route-user-in-dcf-message';
import isCardEnrollError from './is-card-enroll-error';
import isConsumerObjectValid from './is-consumer-object-valid';
import isEmptyObject from './is-empty-object';
import isEmptyString from './is-empty-string';
import isLockedUser from './is-locked-user';
import logger from './logger';

/**
 * Added as part of PSP feature. This function will be called if PSP flag available in checkout request
 * @param {object} xoData
 * @param {Adapter} Adapter from system-sdk-external.js
 * @returns {object} configsToUpdate: what to updated in Adapter this scope,
 * message: to launch DCF or return error
 */

const enrollCardHelper = async (
  Adapter,
  { consumer, dataLayer, encryptedCard, isCardFirstFlow, pId }
) => {
  let message;
  const configsToUpdate = { config: {} };
  //Send error message when consumerIdentity or mobileNumber invalid for enrollcard
  message = isConsumerObjectValid({
    consumer,
    dataLayer,
    pId
  });
  if (!isEmptyObject(message)) {
    return { configsToUpdate, message };
  }
  message = {};

  const mobileNumber = consumer?.mobileNumber ?? '';
  let requestParam = {};
  let response;
  const isLoggedInOtpUser = DcfLaunchData?.customProps?.userInfo?.isLoggedInOtpUser ?? false;
  let panJWE = encryptedCard;
  const isRecognizedUser = DcfLaunchData?.customProps?.userInfo?.isRecognizedUser ?? false;
  const isTncFlow = DcfLaunchData?.customProps?.userInfo?.isTncFlow ?? false;

  //for PSP encryptedCard includes billingAddress also
  let encryptedConsumerObject;
  try {
    encryptedConsumerObject = await encryptConsumerObject(consumer);
  } catch (e) {
    logger('Checkout enrollCard -- Error encrypting consumer object', e);
  }

  const dpaTransactionOptions = getDpaTransactionOptionsForEnroll(
    Adapter.config.dpaTransactionOptions //from init object
  );
  requestParam = {
    accessToken: Adapter.isFullAccessToken ? Adapter.accessToken : undefined,
    dpaTransactionOptions,
    encryptedCard,
    encryptedConsumer: encryptedConsumerObject,
    intentId: Adapter.config?.vIntentId ?? null,
    payloadTypeIndicatorCheckout: Adapter.config.merchantConfig.dataLevel
      ? Adapter.config.merchantConfig.dataLevel
      : DEFAULT_VALUE.PAYLOAD_TYPE_INDICATOR,
    // VCO-14037: must pass srcCorrelationId for any returning user
    srcCorrelationId: isLoggedInOtpUser || isRecognizedUser ? Adapter.config.vIntentId : null,
    srcDpaId: Adapter.config.merchantConfig.srcDpaId ? Adapter.config.merchantConfig.srcDpaId : null
  };

  try {
    response = await EnrollCardService.enrollCard(requestParam, Adapter.config.apikey);
  } catch (errorMessage) {
    const step = `ENROLL_CARD_API_ERROR_RESPONSE`;
    const data = errorMessage || {};
    data.pId = pId;
    message.error = data;
    Adapter.pushEvents({
      E: step,
      ERRCODE: data.httpStatus ? data.httpStatus : null,
      ERRMSG: data.errorMessage ? data.errorMessage : null,
      LL: 'INFO',
      S: 500,
      TY: 'RESPONSE',
      checkout_step: step,
      response: errorMessage,
      srcInitiatorId: Adapter.config.srcInitiatorId
    });
    logger('Checkout -- sending post message back to Visa ...', message);
    message = getCheckoutMessage({
      error: { reason: CARD_ENROLL_FAILURES.CARD_ADD_FAILED },
      pId
    });
    return { configsToUpdate, message };
  }
  if (response.ok) {
    let step = '';
    const { customOutputData, maskedCard, maskedConsumer } = response.data;
    const dpaAccepted = customOutputData?.dpaAccepted === true;

    // For IOC card send error message if recognized user
    if (customOutputData?.isIssuerManaged === true && isRecognizedUser) {
      message = getCheckoutMessage({
        error: { reason: CARD_ENROLL_FAILURES.CARD_ADD_FAILED },
        pId
      });
      return { configsToUpdate, message };
    }
    //To address VCO-14379, unsupported billing address use case
    if (isCardEnrollError(response.data, dpaTransactionOptions)) {
      step = `ENROLL_CARD_API_ERROR_RESPONSE`;
      message = getCheckoutMessage({
        error: { reason: CARD_ENROLL_FAILURES.CARD_ADD_FAILED },
        pId
      });
    } else {
      // if BE returns customOutputData.encryptedCard (when existing user), update dcfLaunchData
      const encryptedCardFromResponse = customOutputData?.encryptedCard;
      if (!isEmptyString(encryptedCardFromResponse)) {
        panJWE = encryptedCardFromResponse;
      }
      // From SDK set route to enroll-card, F20K onwards DCF will decide all custom routing logic
      const toRoute = ENROLL_CARD;
      step = 'ENROLL_CARD_API_SUCCESS_RESPONSE';
      const isLocked = isLockedUser(response.data) || DcfLaunchData.customProps.userInfo.isLocked;
      const panLastFour = maskedCard && maskedCard.panLastFour ? maskedCard.panLastFour : null;
      /**
       * For logged in otp user who didn't pass consumer.consumerIdentity in checkout request,
       * use unmasked email from maskedConsumer object for VCO-14074
       * Per F20000, maskConsumer may not be sent back if no consumer in request, add default value
       */
      const email =
        !isLocked && !isEmptyObject(consumer?.consumerIdentity ?? {})
          ? consumer.consumerIdentity.identityValue
          : maskedConsumer?.maskedConsumerIdentity?.maskedIdentityValue ?? '';

      const srcCorrelationId = response?.data?.srcCorrelationId ?? Adapter.config.vIntentId;

      // if xAuthCode returned in enrollCard response, pass to cfe helper and update state
      const enrollCardXAuthCode =
        response.headers['x-auth-code'] || response.headers['X-AUTH-CODE'];
      if (!isEmptyString(enrollCardXAuthCode)) {
        DcfLaunchData.setXAuthCode(enrollCardXAuthCode);
      }

      //Set available values to DcfLaunchData object prior to transfer
      DcfLaunchData.setUserInfo({
        dpaAccepted,
        email,
        isLocked,
        isTncFlow,
        mobileNumber
      });
      //set transfer object for DCF to process and decide next route
      DcfLaunchData.setTransfer({
        addCardInfo: {
          cardSuffix: panLastFour,
          enrollCardResponse: response.data,
          networkId: VISA,
          panJWE
        }
      });

      /**
       * VCO-11113 edge case: idLookup + CFE flow, system-sdk will have temp accessToken
       *
       * VCO-14530 VCO-14661 if user account is locked we want to honor the `isCardFirstFlow` flag to ensure
       * user can get DCF Add Billing Address screen as part of guest checkout flow. Otherwise check both
       * `isCardFirstFlow` and for presence of the accessToken.
       *
       * VCO-14809 in PSP use cases, we aren't passed isCardFirst flow. In this case we need to derive
       * the value from accessToken
       */

      Object.assign(configsToUpdate.config, {
        isCardFirstFlow:
          isLocked && typeof isCardFirstFlow === 'boolean'
            ? isCardFirstFlow
            : Boolean(isCardFirstFlow) || isEmptyString(Adapter.accessToken),
        isSRCBranded: true,
        toRoute,
        vIntentId: srcCorrelationId
      });

      message = getRouteUserInDCFMessage();
    }
    Adapter.pushEvents({
      E: step,
      ERRCODE: response.httpStatus ? response.httpStatus : null,
      ERRMSG: response.errorMessage ? response.errorMessage : null,
      LL: 'INFO',
      S: 200,
      TY: 'RESPONSE',
      checkout_step: step,
      srcInitiatorId: Adapter.config.srcInitiatorId
    });
    logger('Checkout -- sending post message back to Visa SDK ...', message);
  } else {
    message = getCheckoutMessage({
      error: {
        reason: getErrorMessageFromResponse(response) || CARD_ENROLL_FAILURES.CARD_ADD_FAILED
      },
      pId
    });
  }
  return { configsToUpdate, message };
};

export default enrollCardHelper;
